import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Our Vision",
    text: "To bring timeless elegance and refined luxury into everyday Indian life — not as occasion, but as habit. Zelvoria is a master brand for your everyday luxuries, from candles to jewellery, and beyond.",
  },
  {
    n: "02",
    title: "Our Philosophy",
    text: "Quiet luxury. We believe the most beautiful things never shout. They glow — in the weight of a glass jar, the arc of a flame, the restraint of a label. Every Zelvoria object is designed to be felt before it is noticed.",
  },
  {
    n: "03",
    title: "Our Beginning",
    text: "Zelvoria begins with candles: hand-poured in small batches, blended from premium waxes and composed like fine fragrance. A single flame felt like the right place to begin a house of luxury.",
  },
  {
    n: "04",
    title: "Our Future",
    text: "Soon, exquisite jewellery, timeless fashion and more will join the collection — different categories, one unmistakable Zelvoria identity. Beautiful everyday living, under one name.",
  },
];

export default function About() {
  return (
    <div className="bg-ivory" data-testid="about-page">
      <Seo
        title="The Zelvoria Story — About Us"
        siteName="ZELVORIA"
        description="Quiet luxury. Timeless elegance. Beautiful everyday living. The story of Zelvoria — a master brand beginning with candles."
        image="/images/story.jpg"
      />
      <header className="border-b border-beige/70 bg-parchment">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center md:py-28">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">About</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-4 font-serif text-5xl font-light text-ink md:text-6xl">The Zelvoria Story</h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-6 max-w-xl font-serif text-xl italic text-taupe md:text-2xl">
              Quiet luxury. Timeless elegance. Beautiful everyday living.
            </p>
          </Reveal>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-20 md:py-28">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={0.05}>
            <div className="grid gap-6 border-t border-beige py-14 md:grid-cols-12 md:gap-10" data-testid={`about-chapter-${c.n}`}>
              <p className="font-serif text-5xl font-light text-gold/70 md:col-span-2">{c.n}</p>
              <h2 className="font-serif text-3xl font-light text-ink md:col-span-4">{c.title}</h2>
              <p className="text-sm font-light leading-loose text-taupe md:col-span-6 md:text-base">{c.text}</p>
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.1}>
          <div className="relative mt-10 overflow-hidden">
            <img
              src="/images/story.jpg"
              alt="A warm cream Zelvoria interior with candles, roses and linen-bound books"
              className="h-[55vh] w-full object-cover md:h-[65vh]"
            />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="py-20 text-center md:py-24">
            <div className="mx-auto h-px w-20 bg-gold" />
            <p className="mx-auto mt-10 max-w-2xl font-serif text-2xl font-light italic leading-relaxed text-ink md:text-4xl">
              “What began with candles is just the beginning — one name, one identity, a thousand quiet moments of
              luxury.”
            </p>
            <div className="mx-auto mt-10 h-px w-20 bg-gold" />
            <Link
              to="/candles"
              data-testid="about-explore-button"
              className="group mt-12 inline-flex items-center gap-3 bg-cocoa px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold"
            >
              Explore the First Collection
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
